/**
 * SearchTeamstoresV2 Styled Components
 */
import styled from 'styled-components'
import Skeleton from 'antd/lib/skeleton'
import { TextLink } from '../../theme/components'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  background: var(--neutral-white);
`

export const HeroSection = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 500px;
  min-height: 500px;
  padding: var(--spacing-128) var(--spacing-16);
  overflow: hidden;

  @media (max-width: 768px) {
    height: 368px;
    min-height: 368px;
    padding: var(--spacing-80) var(--spacing-12);
  }
`

export const HeroMedia = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      to bottom,
      rgba(10, 10, 9, 0),
      rgba(10, 10, 9, 0.88)
    );
    mix-blend-mode: multiply;
    pointer-events: none;
  }
`

export const CarouselLoader = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  height: 500px;
  background: var(--neutral-darkest);

  @media (max-width: 768px) {
    height: 368px;
  }
`

export const NavSkeleton = styled(Skeleton)`
  && {
    max-width: 70%;
    margin-bottom: var(--spacing-32);
  }

  /* Paint muted bones immediately — avoid opacity flash after SC/antd styles settle */
  .ant-skeleton-title,
  .ant-skeleton-paragraph > li {
    background: rgba(255, 255, 255, 0.15) !important;
  }

  &.ant-skeleton-active .ant-skeleton-content .ant-skeleton-title,
  &.ant-skeleton-active .ant-skeleton-content .ant-skeleton-paragraph > li {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.08) 25%,
      rgba(255, 255, 255, 0.22) 37%,
      rgba(255, 255, 255, 0.08) 63%
    ) !important;
    background-size: 400% 100% !important;
  }

  .ant-skeleton-title {
    border-radius: var(--radius-full);
    height: 36px;
  }
`

export const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 368px;
  }

  /* Ant Design / slick wrappers only — slide content uses CarouselItem fullHeight */
  .ant-carousel,
  .slick-slider,
  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100% !important;
  }

  .slick-list {
    overflow: hidden;
  }

  .slick-slide > div {
    position: relative;
  }

  img,
  video {
    position: absolute;
    inset: 0;
    width: 100% !important;
    height: 100% !important;
    max-height: none !important;
    object-fit: cover !important;
    object-position: center center !important;
    border-radius: 0 !important;
    filter: none !important;
  }

  & .slick-prev,
  & .slick-next {
    display: none !important;
  }

  .slick-dots {
    bottom: var(--spacing-24);
    left: 0;
    right: 0;
    z-index: 2;
    margin: 0;
    padding: 0;
    li {
      button {
        opacity: 0.6;
        background: none !important;
      }
      button:before {
        opacity: 1;
        color: var(--neutral-border-med);
        filter: drop-shadow(0px 0px 1px white);
        font-size: 12px !important;
      }
      &.slick-active {
        button {
          opacity: 1;
          color: var(--neutral-white) !important;
        }
      }
    }
  }
`

export const SearchBackground = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  object-position: 50% 20%;

  @media (max-width: 768px) {
    height: 368px;
  }
`

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-32);
  width: 100%;
  max-width: 476px;
  text-align: center;

  @media (max-width: 768px) {
    gap: var(--spacing-24);
  }

  #search-bar-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`

export const HeroTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-12);
  width: 100%;
`

export const HeroTitle = styled.h1`
  margin: 0;
  width: 100%;
  color: var(--neutral-white);
  font: 700 72px/74px var(--font-barlow-semi-condensed);
  letter-spacing: -1.6px;
  text-transform: capitalize;

  @media (max-width: 768px) {
    font: 700 40px/42px var(--font-barlow-semi-condensed);
    letter-spacing: 0;
  }
`

export const HeroSubtitle = styled.p`
  margin: 0;
  width: 100%;
  color: var(--neutral-white);
  font: 400 16px/24px var(--font-inter);
  white-space: break-spaces;
  @media (max-width: 768px) {
    font: 400 16px/24px var(--font-inter);
    white-space: normal;
  }
`

interface ResultContainerProps {
  secondary?: boolean
}

export const ResultContainer = styled.div`
  padding: 0 var(--spacing-72) var(--spacing-32) var(--spacing-72);
  background-color: var(--neutral-white);
  ${({ secondary }: ResultContainerProps) =>
    secondary
      ? `
    padding-bottom: 0;
    padding-top: 0;
  `
      : ''}

  @media (max-width: 600px) {
    &:has(> :not(:empty)) {
      padding: 26px var(--spacing-16) var(--spacing-40) var(--spacing-16);
    }
  }
`

export const TitleContainer = styled.div``

export const Title = styled.div`
  height: 33px;
  text-align: center;
  color: var(--neutral-darkest);
  font-size: 20px;
  font-weight: bold;
  line-height: 33px;
`

export const TrustSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-32);
  width: 100%;
  padding: var(--spacing-48) var(--spacing-72);
  background: var(--neutral-lightest-block);

  @media (max-width: 768px) {
    gap: var(--spacing-32);
    padding: var(--spacing-32) var(--spacing-16);
  }
`

export const TrustTitle = styled.h2`
  margin: 0;
  width: 100%;
  color: var(--neutral-darkest);
  font: 600 24px/30px var(--font-barlow-semi-condensed);
  letter-spacing: 0.4px;
  text-align: center;
`

export const PayIcons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  /* 14px row gap: optical match with legacy SVG padding (Figma frame gap is 24) */
  gap: var(--spacing-16) var(--spacing-14);
  width: 100%;
  max-width: 900px;

  @media (max-width: 768px) {
    max-width: 370px;
  }

  .payment-icon {
    flex-shrink: 0;
    object-fit: contain;
    object-position: center;
  }
`

export const PremiumSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-32);
  width: 100%;
  padding: var(--spacing-100) var(--spacing-24);
  background: var(--neutral-white);
  text-align: center;

  @media (max-width: 768px) {
    gap: var(--spacing-24);
    padding: calc(var(--spacing-48) + var(--spacing-32))
      calc(var(--spacing-12) + var(--spacing-24));
  }
`

export const PremiumHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-16);
  width: 100%;
`

export const PremiumEyebrow = styled.p`
  margin: 0;
  color: var(--neutral-secondary);
  font: 600 12px/20px var(--font-barlow);
  letter-spacing: 1px;
  text-transform: uppercase;
`

export const PremiumTitle = styled.h2`
  margin: 0;
  max-width: 526px;
  color: var(--neutral-darkest);
  font: 700 40px/42px var(--font-barlow-semi-condensed);
  text-transform: capitalize;

  @media (max-width: 768px) {
    font: 700 28px/34px var(--font-barlow-semi-condensed);
  }
`

export const PremiumBody = styled.p`
  margin: 0;
  max-width: 739px;
  color: var(--neutral-body);
  font: 400 16px/24px var(--font-inter);
`

export const LearnMoreCta = styled(TextLink)`
  width: auto;
`

export const StatsSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-128);
  width: 100%;
  padding: var(--spacing-48) var(--spacing-72);
  background: var(--neutral-darkest);

  @media (max-width: 1024px) {
    gap: var(--spacing-64);
    padding: var(--spacing-48) var(--spacing-32);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-64);
    padding: var(--spacing-48) var(--spacing-16);
  }
`

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-16);
  text-align: center;
  color: var(--neutral-white);

  .stat-value {
    margin: 0;
    font: 700 40px/42px var(--font-barlow-semi-condensed);
    text-transform: capitalize;
  }

  .stat-label {
    margin: 0;
    font: 500 14px/20px var(--font-barlow);
    letter-spacing: 1px;
    text-transform: uppercase;
  }
`



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/screens/SearchTeamstoresV2/styledComponents.tsx